import { useNavigate } from 'react-router';
import { useState } from 'react';
import ModalAlert from '~/components/modals/ModalAlert/ModalAlert';
import { errorMessages } from '~/constants/errorMessages.ts';
import { Box, Button, Grid, Typography } from '@mui/material';
import { FormProvider, useWatch } from 'react-hook-form';
import { styles } from '~/modules/auth/styles.ts';
import { FormInput } from '~/components/FormComponents/FormInput';
import { useSingUpForm } from '../hooks/useHookForms.ts';
import { FormPhoneField } from '~/components/FormComponents/FormPhoneField.tsx';
import { ConsentToDataProcessing } from '~/modules/auth/sign-up/components/ConsentToDataProcessing.tsx';
import FullPageLoader from '~/components/FullPageLoader/FullPageLoader';
import OTPCodeModal from '~/components/OTPCodeModal/OTPCodeModal.tsx';
import { ERROR_CODES } from '~/constants/errorCodes.ts';
import { InfoTooltip } from '~/components/InfoTooltip/InfoTooltip.tsx';
import { infoMessages } from '~/constants/infoMessages.ts';
import { TSignUp, TSignUpForm } from '../types.ts';
import { FormPassword } from '~/components/FormComponents/FormPassword.tsx';
import { useAppDispatch } from '~/store/appDispatch.ts';
import { TErrorResponse } from '~/store/types.ts';
import { checkBeforeRegisterUser, registerNewUser } from '~/store/users/thunks.ts';
import { getRedirectPath } from '~/modules/auth/authRedirect.ts';
import FormGridItem from '~/components/FormComponents/FormGridItem';
import { commonStyles } from '~/theme/styles/commonStyles.ts';

const SignUp = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const form = useSingUpForm();
  const { isSubmitting } = form.formState;
  const [modalAlertText, setModalAlertText] = useState('');
  const [modalOTPVerification, setModalOTPVerification] = useState<boolean>(false);
  const [otpError, setOtpError] = useState<string>('');
  const phone = useWatch({ control: form.control, name: 'phone' });

  const closeAlertModal = () => {
    setModalAlertText('');
    setModalOTPVerification(false);
  };

  const getRegisterErrorMessage = (error: unknown) => {
    const errorData = (error as { data?: TErrorResponse })?.data;

    return errorData?.errorCode === ERROR_CODES.SUCH_USER_ALREADY_EXISTS
      ? errorData.message
      : errorMessages.SERVICE_UNAVAILABLE;
  };

  const handleValidationSuccess = async () => {
    try {
      const { repeatPassword, checkConsentToDataProcessing, ...formData } = form.getValues();
      await dispatch(registerNewUser({ data: formData as TSignUp })).unwrap();
      setModalOTPVerification(false);
      const redirectPath = getRedirectPath();

      if (redirectPath) {
        navigate('/sign-in', { state: { from: redirectPath } });
        return;
      }

      navigate('/sign-in');
    } catch (error: unknown) {
      setModalAlertText(getRegisterErrorMessage(error));
    }
  };

  const onSubmit = async (values: TSignUpForm) => {
    if (!values.checkConsentToDataProcessing) {
      setModalAlertText(errorMessages.CONSENT_PROCESSING_PERSONAL_DATA);
      return;
    }

    const data = {
      phoneNumber: values.phone,
      username: values.username,
    };

    try {
      await dispatch(checkBeforeRegisterUser(data)).unwrap();
      setModalOTPVerification(true);
    } catch (error: unknown) {
      setModalAlertText(getRegisterErrorMessage(error));
    }
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.containerSignUp}>
        <Typography sx={styles.headerText}>Реєстрація</Typography>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <Grid>
              <FormGridItem>
                <FormInput
                  name="username"
                  label={
                    <Box sx={{ ...commonStyles.centerFlexContainer, gap: 1 }}>
                      <Typography variant="body2">Логін</Typography>
                      <InfoTooltip title={infoMessages.LOGIN_DURING_REGISTRATION} />
                    </Box>
                  }
                  placeholder="Введіть логін"
                  fullWidth
                />
              </FormGridItem>
              <FormGridItem>
                <FormPassword
                  type="password"
                  autoComplete="current-password"
                  name="password"
                  label={
                    <Box sx={{ ...commonStyles.centerFlexContainer, gap: 1 }}>
                      <Typography variant="body2">Пароль</Typography>
                      <InfoTooltip title={infoMessages.PASSWORD_DURING_REGISTRATION} />
                    </Box>
                  }
                  placeholder="Введіть пароль"
                  fullWidth
                />
              </FormGridItem>
              <FormGridItem>
                <FormPassword
                  type="password"
                  autoComplete="current-password"
                  name="repeatPassword"
                  label="Продублюйте пароль"
                  placeholder="Введіть пароль повторно"
                  fullWidth
                />
              </FormGridItem>
              <FormGridItem>
                <FormInput
                  name="email"
                  label="Електронна пошта"
                  placeholder="Введіть електронну пошту"
                  fullWidth
                />
              </FormGridItem>
              <FormGridItem>
                <FormPhoneField name="phone" label="Фінансовий номер телефону" />
              </FormGridItem>
              <FormGridItem>
                <ConsentToDataProcessing />
              </FormGridItem>
            </Grid>

            <OTPCodeModal
              modalOTPVerification={modalOTPVerification}
              phone={phone!}
              error={otpError}
              registrationOnPortal={true}
              setError={setOtpError}
              onValidationSuccess={handleValidationSuccess}
            />

            <ModalAlert isOpen={!!modalAlertText} text={modalAlertText} onClose={closeAlertModal} />
            {isSubmitting && <FullPageLoader />}

            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              sx={{ alignSelf: 'center', marginLeft: '25%' }}
            >
              Зареєструватись
            </Button>
          </form>
        </FormProvider>
      </Box>
    </Box>
  );
};

export default SignUp;
